<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="70%"
    :close-on-click-modal="false"
    :before-close="handleClose"
    class="question-edit-dialog"
  >
    <div class="question-form">
      <el-form :model="form" ref="questionForm" label-width="120px" :rules="rules">
        <!-- 问答类型 -->
        <el-form-item label="问答类型：" prop="faq_type" required>
          <el-select 
            v-model="form.faq_type" 
            placeholder="请选择类型"
            :disabled="isView"
            style="width: 200px;"
          >
            <el-option label="h5客户" :value="1" />
            <el-option label="代理人" :value="2" />
            <el-option label="在线咨询" :value="4" />
          </el-select>
        </el-form-item>

        <!-- 问题 -->
        <el-form-item label="问题：" prop="question" required>
          <el-input
            type="textarea"
            v-model="form.question"
            placeholder="请输入问题(字数不超过30个汉字)"
            :disabled="isView"
            :rows="3"
            maxlength="30"
            show-word-limit
            style="width: 500px;"
          />
        </el-form-item>

        <!-- 答案 -->
        <el-form-item label="答案：" prop="answer" required>
          <el-input
            type="textarea"
            v-model="form.answer"
            placeholder="请输入答案"
            :disabled="isView"
            :rows="6"
            show-word-limit
            style="width: 500px;"
          />
          <div class="answer-tip">
            已输入<span style="color: red;">{{ form.answer.length }}</span>个字
          </div>
        </el-form-item>

        <!-- 备注 -->
        <el-form-item label="备注：">
          <el-input
            v-model="form.note"
            placeholder="请输入备注(不输入为空)"
            :disabled="isView"
            style="width: 500px;"
          />
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button v-if="!isView" type="primary" @click="handleSave" :loading="saving">发 布</el-button>
      <el-button @click="handleCancel">{{ isView ? '关 闭' : '取 消' }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { 
  saveQuestion, 
  getQuestionInfo
} from '@/api/product'

export default {
  name: 'QuestionEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    questionData: {
      type: Object,
      default: null
    },
    productId: {
      type: Number,
      default: null
    },
    isView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      saving: false,
      form: {
        id: null,
        faq_type: null,
        question: '',
        answer: '',
        note: '',
        product_id: null
      },
      rules: {
        faq_type: [
          { required: true, message: '请选择问答类型', trigger: 'change' }
        ],
        question: [
          { required: true, message: '请输入问题', trigger: 'blur' },
          { max: 30, message: '问题不能超过30个字符', trigger: 'blur' }
        ],
        answer: [
          { required: true, message: '请输入答案', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      if (this.isView) return '查看问答信息'
      return this.isEdit ? '编辑问答信息' : '添加问答信息'
    },
    isEdit() {
      return !!(this.questionData && this.questionData.id)
    }
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val
        if (val) {
          this.initDialog()
        }
      },
      immediate: true
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    // 初始化对话框
    async initDialog() {
      if (!this.productId) {
        this.$message.error('请先选择产品再添加问答')
        this.handleCancel()
        return
      }

      this.form.product_id = this.productId

      if (this.questionData && this.questionData.id) {
        await this.loadQuestionData()
      } else {
        this.resetForm()
      }
    },

    // 加载问答数据
    async loadQuestionData() {
      try {
        const response = await getQuestionInfo({ id: this.questionData.id })
        if (response && response.err_code === 0) {
          const data = response.data
          
          this.form.id = data.id
          this.form.faq_type = data.faq_type
          this.form.question = data.question || ''
          this.form.answer = data.answer || ''
          this.form.note = data.note || ''
          this.form.product_id = data.product_id || this.productId
        } else {
          this.$message.error('加载问答数据失败')
        }
      } catch (error) {
        console.error('加载问答数据失败:', error)
        this.$message.error('加载数据失败')
      }
    },

    // 保存
    async handleSave() {
      try {
        await this.$refs.questionForm.validate()
      } catch (error) {
        return
      }

      // 额外验证
      if (!this.form.faq_type) {
        this.$message.warning('请选择问答类型')
        return
      }

      if (!this.form.question.trim()) {
        this.$message.warning('请输入问题')
        return
      }

      if (this.form.question.length > 30) {
        this.$message.warning('问题已超过30个汉字')
        return
      }

      if (!this.form.answer.trim()) {
        this.$message.warning('请输入回答')
        return
      }

      this.saving = true
      try {
        const data = {
          faq_type: this.form.faq_type,
          question: this.form.question.trim(),
          answer: this.form.answer.trim(),
          note: this.form.note.trim(),
          product_id: this.form.product_id
        }

        if (this.form.id) {
          data.id = this.form.id
        } else {
          data.id = 0
        }

        const response = await saveQuestion(data)
        if (response && response.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('save', response.data)
        } else {
          this.$message.error(response?.err_msg || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.saving = false
      }
    },

    // 取消
    handleCancel() {
      this.handleClose()
    },

    // 关闭对话框
    handleClose() {
      this.$emit('cancel')
      this.resetForm()
    },

    // 重置表单
    resetForm() {
      this.form = {
        id: null,
        faq_type: null,
        question: '',
        answer: '',
        note: '',
        product_id: this.productId
      }
      
      if (this.$refs.questionForm) {
        this.$refs.questionForm.clearValidate()
      }
    }
  }
}
</script>

<style scoped>
.question-edit-dialog ::v-deep .el-dialog__body {
  padding: 20px 20px 0 20px;
}

.question-form {
  max-height: 70vh;
  overflow-y: auto;
}

.dialog-footer {
  text-align: center;
  padding: 10px 0;
}

.el-form-item {
  margin-bottom: 20px;
}

.answer-tip {
  margin-top: 5px;
  text-align: right;
  font-size: 12px;
  color: #666;
}

/* 表单样式优化 */
.question-edit-dialog ::v-deep .el-form-item__label {
  font-weight: normal;
}

.question-edit-dialog ::v-deep .el-textarea__inner {
  font-family: inherit;
  resize: vertical;
}

.question-edit-dialog ::v-deep .el-input__count {
  background: transparent;
}
</style>